import type * as arrow from "apache-arrow";

export type ContainerTotals = Readonly<{
  total: number;
  byLocation: ReadonlyArray<Readonly<{ location: string; count: number }>>;
}>;

// The transform drops the long container_id key, and the bare label ("C04")
// repeats across lanes. container_id is built from supplier / location /
// order_placed_date + label, so that tuple identifies one physical box. At SKU
// grain a box spans several rows, so count distinct keys, never rows.
const KEY_COLUMNS = [
  "container_label",
  "location",
  "supplier",
  "order_placed_date",
] as const;

/**
 * Distinct containers in the (already filter-applied) container_mapping
 * output, overall and per destination location. Returns null when the table
 * lacks the columns needed to identify a box.
 */
export const countContainers = (
  table: arrow.Table | null | undefined,
): ContainerTotals | null => {
  if (!table) return null;
  const vectors = KEY_COLUMNS.map((name) => table.getChild(name));
  if (vectors.some((v) => v == null)) return null;
  const [labels, locations, suppliers, placedDates] = vectors as arrow.Vector[];

  const seen = new Set<string>();
  const perLocation = new Map<string, number>();
  for (let i = 0; i < table.numRows; i++) {
    const label = labels.get(i);
    if (label == null) continue;
    const location = String(locations.get(i) ?? "");
    const key = [label, location, suppliers.get(i), placedDates.get(i)]
      .map((v) => String(v ?? ""))
      .join("\u0000");
    if (seen.has(key)) continue;
    seen.add(key);
    perLocation.set(location, (perLocation.get(location) ?? 0) + 1);
  }

  const byLocation = Array.from(perLocation, ([location, count]) => ({
    location,
    count,
  })).sort(
    (a, b) => b.count - a.count || a.location.localeCompare(b.location),
  );

  return { total: seen.size, byLocation };
};
